import dayjs from "dayjs";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { stringMaxLength } from "../../../assets/validation/common";
import { ContractorTypes } from "../../../models/emums/contractorTypes";
import ContractorPolicyRestModel from "../../../models/rest/contractor/contractorPolicyRestModel";
import ContractorRestModel from "../../../models/rest/contractor/contractorRestModel";
import CountryRestModel from "../../../models/rest/register/countryRestModel";
import ConfirmDialog from "../../../layouts/confirmDialog/confirmDialog.vue";

@Component({
    components: {
        "confirm-dialog": ConfirmDialog,
    }
})
export default class ContractorDialog extends Vue {
    policyTermLabel = this.$t("contractor.expiration");
    showPolicyError = false;
    tinValidationLoading = false;

    validation = {
        symbol: [
            stringMaxLength(this.$i18n, this.$t("contractor.symbol"), 30),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        name: [
            stringMaxLength(this.$i18n, this.$t("contractor.name"), 254),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        streetAndNumber: [
            stringMaxLength(this.$i18n, this.$t("contractor.streetAndNumber"), 254),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        country: [
            stringMaxLength(this.$i18n, this.$t("contractor.country"), 40),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        postalCode: [
            stringMaxLength(this.$i18n, this.$t("contractor.postalCode"), 10),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        city: [
            stringMaxLength(this.$i18n, this.$t("contractor.city"), 100),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        email: [
            stringMaxLength(this.$i18n, this.$t("contractor.email"), 95)
        ],
        specialInfo: [
            stringMaxLength(this.$i18n, this.$t("contractor.specialInfo"), 2048)
        ],
        tin: [
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        accountManagers: [
            (value: string[]) => (value && value.length > 0) || this.$t("common.validationErrorRequired")
        ],
    }

    policyListHeaders = [
        {
            text: this.$t("contractor.type"),
            align: "start",
            value: "type",
            sortable: false
        },
        {
            text: this.$t("contractor.policyRegion"),
            align: "start",
            value: "region",
            sortable: false
        },
        {
            text: this.$t("contractor.insurer"),
            align: "start",
            value: "policyIssuerName",
            sortable: false
        },
        {
            text: this.$t("contractor.value"),
            align: "start",
            value: "value",
            sortable: false
        },
        {
            text: this.$t("contractor.currency"),
            align: "start",
            value: "valueCurrency",
            sortable: false
        },
        {
            text: this.$t("contractor.expiration"),
            align: "start",
            value: "policyTerm",
            sortable: false
        },
        {
            text: this.$t("contractor.hasPolicyAttachment"),
            align: "center",
            value: "hasPolicyAttachment",
            sortable: false
        },
        {
            text: this.$t("contractor.hasPolicyConfirmation"),
            align: "center",
            value: "hasPolicyConfirmation",
            sortable: false
        },
        {
            text: this.$t("contractor.actions"),
            align: "right",
            value: "actions",
            width: "1%",
            sortable: false
        }
    ];

    get isLoading(): boolean {
        return this.$store.direct.state.loading;
    }

    get showDialog(): boolean {
        return this.$store.direct.state.supervisor.showContractorDialog;
    }

    get title(): string {
        return this.$store.direct.state.supervisor.contractorDialogTitle;
    }

    get contractorTypes(): string[] {
        return this.$store.direct.state.supervisor.contractorDialogContractorTypes;
    }

    get contractorIsEdited(): boolean {
        return this.$store.direct.state.supervisor.contractorDialogContractorIsEdited;
    }

    get contractor(): ContractorRestModel {
        const contractor = this.$store.direct.state.contractor.contractor as ContractorRestModel;
        if (Object.keys(contractor).length > 0) {
            contractor.typeIsCarrier = this.contractorTypes.includes(ContractorTypes.Carrier) ? true : false;
            contractor.typeIsClient = this.contractorTypes.includes(ContractorTypes.Client) ? true : false;
            contractor.paymentTermPurchase = 60;
            contractor.paymentTermSale = 30;
            contractor.purchaseValueCurrency = "EUR";
            contractor.saleValueCurrency = "EUR";
        }
        return contractor;
    }

    set contractor(val: ContractorRestModel) {
        this.$store.direct.commit.contractor.setContractor(val);
    }

    get countryList(): CountryRestModel[] {
        return this.$store.direct.state.contractor.contractorAssets.countries;
    }

    get policyList(): ContractorPolicyRestModel[] {
        return this.$store.direct.state.contractor.policyList;
    }

    get showPolicyRegion(): boolean {
        if (this.contractorTypes.includes(ContractorTypes.Carrier)) {
            return true;
        }
        return false;
    }

    get contractorIsClient(): boolean {
        if (this.contractorTypes.includes(ContractorTypes.Client)) {
            if (!this.contractorIsEdited) {
                this.contractor.accountManagers = [] as string[];
            }
            return true;
        }
        return false;
    }

    policyEditClick(item: ContractorPolicyRestModel) {
        this.$store.direct.commit.contractor.setContractorPolicyIsTemporary(false);
        this.$store.direct.dispatch.contractor.loadPolicy(item);
        this.$store.direct.commit.contractor.setShowPolicyDialog(true);
    }

    async policyCreateClick() {
        this.$store.direct.commit.contractor.setContractorPolicyIsTemporary(true);
        this.showPolicyError = false;
        await this.$store.direct.dispatch.contractor.loadPolicy();
        this.$store.direct.commit.contractor.setShowPolicyDialog(true);
    }

    get contractorAssets() {
        return this.$store.direct.state.contractor.contractorAssets;
    }

    get userListSorted(): any {
        return this.contractorAssets.crmUsers?.sort((prev, next) => (prev.text > next.text ? 1 : -1));
    }

    get onlyCrmUsers(): any {
        return this.contractorAssets.onlyCrmUsers?.sort((prev, next) => (prev.text > next.text ? 1 : -1));
    }

    get accountManagersItems(): any[] {
        if (this.$route.params.id == undefined && this.contractor.typeIsClient) {
            return this.onlyCrmUsers;
        }
        return this.userListSorted;
    }

    get tinIsValidatedInGus(): boolean | null {
        return this.contractor.isValidatedInGus ?? null;
    }

    get selectedCountryIsInUE(): boolean {
        return this.countryList?.find((x: CountryRestModel) => x.code == this.contractor.tinPrefix)?.isInUE ?? false;
    }

    get tinIsValidatedInVies(): boolean | null {
        return this.contractor.isValidatedInVies ?? null;
    }

    get tinValidationStatus(): boolean | null {
        if (this.contractor.tinPrefix === "PL") return this.tinIsValidatedInGus;
        if (this.selectedCountryIsInUE) return this.tinIsValidatedInVies;
        return null;
    }

    get tinValidationTooltipText(): string {
        if (this.tinValidationLoading) {
            return this.contractor.tinPrefix === "PL"
                ? this.$t("contractor.tinValidatingInGus").toString()
                : this.$t("contractor.tinValidatingInVies").toString();
        }
        if (this.tinValidationStatus === true) {
            return this.contractor.tinPrefix === "PL"
                ? this.$t("contractor.tinValidatedInGus").toString()
                : this.$t("contractor.tinValidatedInVies").toString();
        }
        return this.contractor.tinPrefix === "PL"
            ? this.$t("contractor.tinNotValidatedInGus").toString()
            : this.$t("contractor.tinNotValidatedInVies").toString();
    }

    onTinInput(value: string) {
        this.contractor.tin = value.replace(/\s/g, "");
    }

    async tinBlur() {
        if (!this.contractor.tin) return;
        if (!this.contractor.typeIsClient && !this.contractor.typeIsCarrier) return;

        if (this.contractor.tinPrefix === "PL") {
            await this.validateTinInGus();
        } else if (this.selectedCountryIsInUE) {
            await this.validateTinInVies();
        }
    }

    private async validateTinInGus() {
        this.tinValidationLoading = true;
        try {
            await this.$store.direct.dispatch.contractor.validateTinInGus({
                contractorId: this.contractor.id,
                tin: this.contractor.tin,
            });
            if (this.tinIsValidatedInGus === false) {
                this.showTinNotValidatedDialog(this.$t("contractor.tinNotValidatedInGus").toString());
            }
        } finally {
            this.tinValidationLoading = false;
        }
    }

    private async validateTinInVies() {
        this.tinValidationLoading = true;
        try {
            await this.$store.direct.dispatch.contractor.validateTinInVies({
                contractorId: this.contractor.id,
                tin: this.contractor.tin,
                countryCode: this.contractor.tinPrefix,
            });
            if (this.tinIsValidatedInVies === false) {
                this.showTinNotValidatedDialog(this.$t("contractor.tinNotValidatedInVies").toString());
            }
        } finally {
            this.tinValidationLoading = false;
        }
    }

    private showTinNotValidatedDialog(message: string) {
        this.$store.direct.dispatch.confirm.openConfirmDialog({
            showConfirmDialog: true,
            title: message,
            message: message,
            links: [],
            options: { buttonColor: "error", layoutColor: "error" },
            buttonType: "ok"
        });
    }

    formatSingleDateTimeValue(time: any) {
        return time != null ? dayjs(time).format("YYYY-MM-DD HH:mm") : "";
    }

    fillContractorName(val: string) {
        this.contractor.name = val;
    }

    async checkIfContractorTinExists(buttonType: string): Promise<boolean> {
        let contractorTinExists = await this.$store.direct.dispatch.contractor.checkIfContractorTinExists({ contractorTin: this.contractor.tin, contractorId: this.contractor.id });

        const buttonTypeIsYesNo = buttonType == "yes/no";

        if (contractorTinExists) {
            await this.$store.direct.dispatch.confirm
                .openConfirmDialog({
                    showConfirmDialog: true,
                    title: this.$t("contractor.contractorWithThisTinAlreadyExists").toString(),
                    message: (buttonTypeIsYesNo ? this.$t("contractor.contractorWithThisTinAlreadyExistsAreYouSureToSetUpNewOne").toString() : this.$t("contractor.contractorWithThisTinAlreadyExists").toString()) + ": " + this.contractor.tin,
                    links: [],
                    options: { buttonColor: "error", layoutColor: "error" },
                    buttonType: buttonType
                })
                .then(confirm => {
                    if (confirm && buttonTypeIsYesNo) {
                        contractorTinExists = false;
                    }
                });
        }
        return contractorTinExists;
    }

    cancelClick() {
        this.showPolicyError = false;
        this.$store.direct.dispatch.supervisor.isNotConfirmedContractorDialog();
    }

    async saveClick() {
        let contractorTinExists = false;
        if (!this.contractorIsEdited && this.contractor.typeIsCarrier) {
            contractorTinExists = await this.checkIfContractorTinExists("ok");
        }
        else if (this.contractor.typeIsClient) {
            contractorTinExists = await this.checkIfContractorTinExists("yes/no");
        }

        const valid = (this.$refs.form as Vue & { validate: () => boolean }).validate();


        if (this.showPolicyRegion && this.policyList.length === 0) {
            this.showPolicyError = true;
            return;
        }

        if (valid && !contractorTinExists) {
            await this.$store.direct.dispatch.supervisor.isConfirmedContractorDialog();
            await this.$store.direct.dispatch.contractor.saveContractor();
        }
    }
}
